import cn from 'classnames';
import { useState, Dispatch, SetStateAction, MouseEvent as MouseEventGeneric, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { IKnmPreview } from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import { captchaSetActionAfterTokenLoad, captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCoreScreenSize } from 'CorePackage/MainModule/selectors/coreSelectors';
import { DropdownButton } from 'UIPackage/MainModule/components/DropdownButton';
import { IconMenu } from 'UIPackage/MainModule/components/IconMenu';

import { passportKnmExportPdf } from '../actions/passportKnmActions';
import { getPassportKnmData } from '../reducers/passportKnmReducer';
import styles from './KnmHeader.module.scss';
import { feedbackSetKnmData } from '../../../FeedbackPackage/MainModule/actions/feedbackActions';
import { IFeedbackKnmData } from '../../../FeedbackPackage/MainModule/interfaces/IFeedbackKnmData';
import { approveRequiredValues } from '../constants/approveRequiredValues';
import { ROUTE_FEEDBACK } from '../../../CorePackage/MainModule/constants/routes';

interface IKnmHeaderProps {
  activeTab: string;
  setActiveTab: Dispatch<SetStateAction<string>>;
  hasViolation?: boolean;
}

interface IKnmHeaderStoreProps {
  screenSize: string;
  captchaToken: string | null;
  passportData?: IKnmPreview;
}

const KnmHeaderComponent = ({
  activeTab,
  setActiveTab,
  hasViolation,
  captchaToken,
  screenSize,
  passportData,
}: IKnmHeaderProps & IKnmHeaderStoreProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { token: tokenFromParams } = useParams<{ token?: string }>();

  const isErknm = passportData?.version === 'ERKNM';
  const isErpPm = passportData?.version === 'ERP' && passportData?.pmErknm;
  const isPm = (isErknm && passportData?.pmErknm) || isErpPm;
  const resultOfInspectionPm = passportData?.pmErknm && passportData?.pmErknm?.resultOfInspection;

  const tabs = [
    {
      name: 'common',
      text: 'Общая информация',
    },
    {
      name: 'control',
      text: 'Информация об органе контроля',
    },
    {
      name: 'prosecutor',
      text: 'Информация об органе прокуратуры',
    },
    {
      name: 'face',
      text: isErknm || isErpPm ? 'Сведения о контролируемом лице' : 'Информация о проверяемом лице',
    },
    isErknm && tokenFromParams
      ? {
          name: 'act',
          text: 'Сведения об акте',
        }
      : undefined,
    isErknm && tokenFromParams
      ? {
          name: 'resultDecisions',
          text: isPm
            ? 'Сведения о решениях, принятых по результатам ПМ'
            : 'Сведения о решениях, принятых по результатам КНМ',
        }
      : undefined,
    {
      name: 'result',
      text: 'Список результатов',
    },
    {
      name: 'violation',
      text: 'Список выявленных нарушений',
    },
  ];
  const [isRollUp, setIsRollUp] = useState(true);
  const isMobile = screenSize === 'mobile';

  const onExportPdf = () => {
    dispatch(captchaSetActionAfterTokenLoad(passportKnmExportPdf));
    if (!captchaToken) {
      dispatch(captchaSetIsActive(true));
    } else {
      dispatch(passportKnmExportPdf());
    }
  };

  const onClick = (e: MouseEventGeneric<HTMLButtonElement, MouseEvent> & { target: { id: string } }) =>
    setActiveTab(e.target.id);
  const onMenuButtonClick = () => setIsRollUp(prev => !prev);

  const renderMenuButton = () => (
    <button type="button" className={cn({ [styles.MenuButtonIsRollUp]: !isRollUp })} onClick={onMenuButtonClick}>
      <IconMenu />
    </button>
  );

  const problemsRoute = useCallback(() => {
    history.push(ROUTE_FEEDBACK);

    if (passportData) {
      const knmFeedbackData: IFeedbackKnmData = {
        id: passportData.id,
        erpId: passportData.erpId,
        status: passportData.status,
        typeName: passportData.typeName,
        signed: passportData.signed,
        kindKnm: passportData.inspectionErknm?.kindKnm,
        approveRequired: approveRequiredValues.find(
          item => item.id === passportData?.inspectionErknm?.approve?.approveRequired,
        )?.title,
      };

      dispatch(feedbackSetKnmData(knmFeedbackData));
    }
  }, [dispatch, passportData, history]);

  const dropDownItems =
    isErknm && !isPm
      ? [
          {
            id: 'exportToPdf',
            content: (
              <span className="shared-dropdown-button" onClick={onExportPdf}>
                Экспорт в PDF
              </span>
            ),
          },
          {
            id: 'knmProblem',
            content: (
              <span className="shared-dropdown-button" onClick={problemsRoute}>
                Проблема с КНМ
              </span>
            ),
          },
        ]
      : [
          {
            id: 'exportToPdf',
            content: (
              <span className="shared-dropdown-button" onClick={onExportPdf}>
                Экспорт в PDF
              </span>
            ),
          },
        ];

  const renderTabs = () => {
    if (isMobile && isRollUp) {
      const tab = tabs.find(x => activeTab === x?.name);
      return (
        <div className={styles.MobileRolledMenu}>
          <button
            key={tab?.name}
            className={cn(styles.TabItem, { [styles.TabActive]: true })}
            id={tab?.name}
            onClick={onClick}
            type="button"
            style={{ outline: 'none' }}
          >
            {tab?.text}
          </button>
          {renderMenuButton()}
        </div>
      );
    }
    return (
      <>
        <div className={styles.tabsWrapper}>
          {tabs.map(tab => {
            if (!tab) {
              return null;
            }
            if ((tab.name === 'violation' && !hasViolation) || !tab) {
              return null;
            }
            if (((isErknm && !isPm) || (isPm && !resultOfInspectionPm)) && tab.name === 'result') {
              return null;
            }
            return (
              <button
                key={tab.name}
                className={cn(styles.TabItem, { [styles.TabActive]: activeTab === tab.name })}
                id={tab.name}
                onClick={onClick}
                type="button"
                style={{ outline: 'none' }}
              >
                {tab.text}
              </button>
            );
          })}
        </div>
        <DropdownButton items={dropDownItems} />
      </>
    );
  };

  return (
    <div className={styles.KnmHeader}>
      <div
        className={cn(styles.Buttons, {
          [styles.IsRollUp]: isRollUp,
        })}
      >
        {renderTabs()}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  screenSize: getCoreScreenSize(state),
  captchaToken: getCaptchaToken(state),
  passportData: getPassportKnmData(state),
});

export const KnmHeader = connect(mapStateToProps)(KnmHeaderComponent);
